import { useEffect, useRef, useState } from "react"
import { IconDownload, IconFileExport, IconRefresh, IconRotateClockwise } from "@tabler/icons-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { useBenefitEditRegistration } from "@/components/benefit/benefit-edit-scope"
import { currentApplicableProgramYear } from "@/lib/program-year"
import { Label } from "@/components/ui/label"
import { api } from "@/lib/api"

interface AsciiCapability {
  enabled: boolean
  disabledReason: string | null
  supportedPrograms: string[]
}

interface AsciiOperation {
  operationId: string
  state: string
  uploadType: string
  benefitProgramId: number
  benefitProgramYear: number
  cohort: string
  certificationStartDate: string | null
  certificationEndDate: string | null
  candidateCount: number
  inputHash: string
  artifactFileName: string | null
  artifactSize: number | null
  committedAt: string | null
  failureCode: string | null
}

type AsciiProgramOption = "Pse" | "Liheap" | "Sheap"

const PROGRAM_OPTIONS: { value: AsciiProgramOption; label: string }[] = [
  { value: "Pse", label: "PSE HELP" },
  { value: "Liheap", label: "LIHEAP" },
  { value: "Sheap", label: "SHEAP" },
]

function programLabel(uploadType: string): string {
  const normalized = uploadType.trim().toLowerCase()
  if (normalized === "pse") return "PSE HELP"
  if (normalized === "liheap") return "LIHEAP"
  if (normalized === "sheap") return "SHEAP"
  return uploadType
}

/** The bounded, default-off certified export surface. All permissions and cohort selection remain server-owned. */
export default function AsciiExportsPage() {
  const [capability, setCapability] = useState<AsciiCapability | null>(null)
  const [operation, setOperation] = useState<AsciiOperation | null>(null)
  const [uploadType, setUploadType] = useState<AsciiProgramOption>("Pse")
  const [year, setYear] = useState(() => String(currentApplicableProgramYear()))
  const [certificationStartDate, setCertificationStartDate] = useState("")
  const [certificationEndDate, setCertificationEndDate] = useState("")
  const [operationKey, setOperationKey] = useState<string>(() => crypto.randomUUID())
  const [explicitReExport, setExplicitReExport] = useState(false)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const register = useBenefitEditRegistration()
  const [error, setError] = useState<string | null>(null)

  const dateRangeInvalid = Boolean(
    certificationStartDate &&
    certificationEndDate &&
    certificationStartDate > certificationEndDate,
  )

  const supportedPrograms = (() => {
    const reported = new Set((capability?.supportedPrograms ?? []).map((item) => item.toLowerCase()))
    const filtered = PROGRAM_OPTIONS.filter((option) => reported.has(option.value.toLowerCase()))
    return filtered.length > 0 ? filtered : PROGRAM_OPTIONS
  })()

  const loadCapability = async () => {
    try {
      const next = await api.get<AsciiCapability>("/api/ascii/v2/capability")
      setCapability(next)
      const reported = new Set((next.supportedPrograms ?? []).map((item) => item.toLowerCase()))
      const available = PROGRAM_OPTIONS.filter((option) => reported.has(option.value.toLowerCase()))
      if (available.length > 0 && !available.some((option) => option.value === uploadType)) {
        setUploadType(available[0]!.value)
        setOperationKey(crypto.randomUUID())
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to read export capability")
    }
  }

  useEffect(() => {
    void loadCapability()
  }, [])

  const run = async (action: () => Promise<void>, fallback: string) => {
    if (inFlight.current) return
    if (!register(true)) { setError("Save or cancel the open editor before continuing."); return }
    inFlight.current = true
    setBusy(true)
    setError(null)
    try { await action() }
    catch (cause) { setError(cause instanceof Error ? cause.message : fallback) }
    finally { inFlight.current = false; setBusy(false); register(false) }
  }

  const create = () => run(async () => {
    const created = await api.post<AsciiOperation>("/api/ascii/v2/batches", {
      uploadType,
      benefitProgramYear: Number(year),
      operationKey,
      cohort: "certified",
      certificationStartDate: certificationStartDate || undefined,
      certificationEndDate: certificationEndDate || undefined,
      explicitReExport,
    })
    setOperation(created)
  }, "Unable to create export operation")

  const refresh = () => run(async () => {
    if (operation) setOperation(await api.get<AsciiOperation>(`/api/ascii/v2/batches/${operation.operationId}`))
  }, "Unable to refresh operation")

  const retry = () => run(async () => {
    if (operation) setOperation(await api.post<AsciiOperation>(`/api/ascii/v2/batches/${operation.operationId}/retry`))
  }, "Unable to retry operation")

  const download = () => run(async () => {
    if (operation) await api.download(`/api/ascii/v2/batches/${operation.operationId}/artifact`, operation.artifactFileName ?? "ascii-v2.zip")
  }, "Unable to download artifact")

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <IconFileExport className="size-7" />
        <div>
          <h1 className="text-2xl font-semibold">Certified ASCII exports</h1>
          <p className="text-muted-foreground">PSE HELP, LIHEAP, and SHEAP. Selection, identifiers, and certification facts are server-owned.</p>
        </div>
      </div>

      {error && <Alert variant="destructive"><AlertTitle>Export request failed</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      {capability && !capability.enabled && (
        <Alert><AlertTitle>Export is disabled</AlertTitle><AlertDescription>{capability.disabledReason ?? "The server has not enabled this capability."}</AlertDescription></Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Prepare a certified {programLabel(uploadType)} batch</CardTitle>
          <CardDescription>The operation is private and idempotent. No preview or download changes Uploaded membership.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid max-w-2xl gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="ascii-program">Program</Label>
              <select
                id="ascii-program"
                aria-label="Program"
                disabled={busy}
                value={uploadType}
                onChange={(event) => {
                  setUploadType(event.target.value as AsciiProgramOption)
                  setOperationKey(crypto.randomUUID())
                }}
                className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
              >
                {supportedPrograms.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2"><Label htmlFor="ascii-year">Program year</Label><Input id="ascii-year" inputMode="numeric" disabled={busy} value={year} onChange={(event) => { setYear(event.target.value); setOperationKey(crypto.randomUUID()) }} /></div>
            <div className="space-y-2"><Label htmlFor="ascii-key">Operation key</Label><Input id="ascii-key" disabled={busy} value={operationKey} onChange={(event) => setOperationKey(event.target.value)} /></div>
          </div>
          <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="ascii-cert-start">Certification date from (optional)</Label>
              <Input
                id="ascii-cert-start"
                type="date"
                disabled={busy}
                value={certificationStartDate}
                onChange={(event) => {
                  setCertificationStartDate(event.target.value)
                  setOperationKey(crypto.randomUUID())
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ascii-cert-end">Certification date through (optional)</Label>
              <Input
                id="ascii-cert-end"
                type="date"
                disabled={busy}
                value={certificationEndDate}
                onChange={(event) => {
                  setCertificationEndDate(event.target.value)
                  setOperationKey(crypto.randomUUID())
                }}
              />
            </div>
          </div>
          {dateRangeInvalid && (
            <p role="alert" className="text-destructive text-sm">The certification start date must be on or before the end date.</p>
          )}
          <label className="flex items-center gap-2 text-sm"><Checkbox disabled={busy} checked={explicitReExport} onCheckedChange={(value) => { setExplicitReExport(value === true); setOperationKey(crypto.randomUUID()) }} /> Explicit re-export of an already included frozen cohort</label>
          <Button disabled={busy || capability === null || capability.enabled === false || !operationKey.trim() || dateRangeInvalid} onClick={() => void create()}><IconFileExport className="mr-2 size-4" />Create operation</Button>
        </CardContent>
      </Card>

      {operation && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2">Operation status <Badge variant={operation.state === "Committed" ? "default" : operation.state === "Failed" || operation.state === "Orphaned" ? "destructive" : "secondary"}>{operation.state}</Badge></CardTitle><CardDescription className="font-mono">{operation.operationId}</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid gap-2 text-sm sm:grid-cols-4"><div><dt className="text-muted-foreground">Program</dt><dd>{programLabel(operation.uploadType)} · PY {operation.benefitProgramYear}</dd></div><div><dt className="text-muted-foreground">Certification dates</dt><dd>{operation.certificationStartDate || operation.certificationEndDate ? `${operation.certificationStartDate ?? "Any"} – ${operation.certificationEndDate ?? "Any"}` : "All dates"}</dd></div><div><dt className="text-muted-foreground">Certified candidates</dt><dd>{operation.candidateCount}</dd></div><div><dt className="text-muted-foreground">Artifact</dt><dd>{operation.artifactFileName ?? "Not committed"}</dd></div><div><dt className="text-muted-foreground">Failure</dt><dd>{operation.failureCode ?? "—"}</dd></div></dl>
            <div className="flex flex-wrap gap-2"><Button variant="outline" disabled={busy} onClick={() => void refresh()}><IconRefresh className="mr-2 size-4" />Refresh</Button>{(operation.state === "Failed" || operation.state === "Orphaned") && <Button variant="outline" disabled={busy} onClick={() => void retry()}><IconRotateClockwise className="mr-2 size-4" />Retry</Button>}{operation.state === "Committed" && <Button disabled={busy} onClick={() => void download()}><IconDownload className="mr-2 size-4" />Download private artifact</Button>}</div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
